import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { FileText, CheckCircle2, Plane, HeartPulse } from 'lucide-react';
export function PatientGuide() {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-1192">
      <Helmet data-id="element-1193">
        <title data-id="element-1194">Patient Guide | Affordable Medical Tourism</title>
        <meta name="description" content="A comprehensive guide for patients considering medical tourism. Learn how to prepare, what to expect, and how we support your journey." data-id="element-1195" />
        <link rel="canonical" href="https://affordablemedicaltourism.com/patient-guide" data-id="element-1196" />
      </Helmet>

      <AppHeader data-id="element-1197" />

      <main className="flex-1 py-20 px-6 md:px-12 max-w-5xl mx-auto w-full" data-id="element-1198">
        <div className="text-center mb-16" data-id="element-1199">
          <h1 className="text-4xl md:text-5xl font-serif text-slate-900 mb-6" data-id="element-1200">
            Patient Guide
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-1201">
            Your comprehensive resource for navigating international healthcare.
            We provide the information you need to make confident, informed
            decisions about your medical journey.
          </p>
        </div>

        <div className="space-y-12" data-id="element-1202">
          {/* Section 1 */}
          <section data-id="element-1203">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-1204">
              <FileText className="w-8 h-8 text-blue-600" data-id="element-1205" />
              Preparation & Planning
            </h2>
            <Card data-id="element-1206">
              <CardContent className="pt-6 prose prose-slate prose-lg max-w-none" data-id="element-1207">
                <p data-id="element-1208">
                  Thorough preparation is the cornerstone of a successful
                  medical journey abroad. Before committing to any procedure, it
                  is crucial to gather all relevant medical records, including
                  recent diagnostic imaging, laboratory results, and notes from
                  your primary care physician. Our care coordinators will
                  securely transmit these documents to the international medical
                  team for a preliminary assessment.
                </p>
                <p data-id="element-1209">
                  During this phase, you will receive a detailed proposal
                  outlining the recommended treatment plan, the credentials of
                  the attending surgeon, and a transparent breakdown of all
                  associated costs. We encourage patients to schedule a virtual
                  consultation with the international specialist to discuss the
                  procedure, ask questions, and establish a rapport before
                  traveling.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Section 2 */}
          <section data-id="element-1210">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-1211">
              <Plane className="w-8 h-8 text-blue-600" data-id="element-1212" />
              Travel & Logistics
            </h2>
            <Card data-id="element-1213">
              <CardContent className="pt-6 prose prose-slate prose-lg max-w-none" data-id="element-1214">
                <p data-id="element-1215">
                  Managing international travel while anticipating a medical
                  procedure can be stressful. Our logistical support is designed
                  to eliminate this burden. We assist with identifying optimal
                  flight routes and securing necessary visas—including long-stay
                  or medical visas for extended treatments.
                </p>
                <p data-id="element-1216">
                  To ensure a seamless experience, we partner with trusted local
                  care facilitators. These on-the-ground experts manage your
                  airport transfers, provide translation services, and arrange
                  comfortable, accessible accommodations. Whether you are
                  staying in a hotel or a specialized recovery retreat in a
                  designated medical tourism city, our facilitators ensure your
                  environment is optimized for healing.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Section 3 */}
          <section data-id="element-1217">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-1218">
              <HeartPulse className="w-8 h-8 text-blue-600" data-id="element-1219" />
              Treatment & Recovery
            </h2>
            <Card data-id="element-1220">
              <CardContent className="pt-6 prose prose-slate prose-lg max-w-none" data-id="element-1221">
                <p data-id="element-1222">
                  Your treatment will take place in an internationally
                  accredited facility, adhering to rigorous clinical protocols.
                  Following the procedure, the initial phase of your recovery
                  will be closely monitored within the hospital. Once
                  discharged, you will transition to your hotel or a specialized
                  recovery retreat.
                </p>
                <p data-id="element-1223">
                  Our local support team remains available to assist with
                  follow-up appointments, medication management, and any
                  immediate needs during your recovery. We ensure you are
                  medically cleared by your surgeon before embarking on your
                  return flight.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Section 4 */}
          <section data-id="element-1224">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3" data-id="element-1225">
              <CheckCircle2 className="w-8 h-8 text-blue-600" data-id="element-1226" />
              Post-Operative Care
            </h2>
            <Card data-id="element-1227">
              <CardContent className="pt-6 prose prose-slate prose-lg max-w-none" data-id="element-1228">
                <p data-id="element-1229">
                  Continuity of care is vital. Before you leave the destination,
                  you will be provided with a comprehensive medical dossier,
                  translated into English, detailing the procedure performed and
                  post-operative instructions.
                </p>
                <p data-id="element-1230">
                  We strongly advise establishing a relationship with a local
                  healthcare provider in your home country prior to your trip.
                  This ensures a seamless transition for ongoing monitoring and
                  any necessary follow-up care upon your return. Our team
                  remains available to facilitate communication between your
                  international surgeon and your local physician if required.
                </p>
              </CardContent>
            </Card>
          </section>
        </div>

        <div className="mt-16 text-center" data-id="element-1231">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-1232">
            Ready to take the next step?
          </h2>
          <p className="text-slate-600 mb-8" data-id="element-1233">
            Our care coordinators are here to guide you through every phase of
            the process.
          </p>
          <Button size="lg" onClick={() => navigate('/contact')} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8" data-id="element-1234">
            Get a Free Consultation
          </Button>
        </div>
      </main>

      <AppFooter data-id="element-1235" />
    </div>;
}